/**
 * Single source of truth for item status -> dot colour, shared by the
 * requirements tree (RequirementsTree.jsx) and the advanced item tooltip
 * (components/itemCard/*) so a status never renders a different colour in
 * one place than another.
 *
 * Covers both the item lifecycle status (draft/approved/implemented/verified)
 * and verification-style statuses (pass/fail/blocked/...) some item types use.
 */
export const STATUS_TONE_CLASS = {
  draft: 'muted', pending: 'muted', unverified: 'muted', review: 'muted',
  approved: 'accent',
  implemented: 'warning', partial: 'warning',
  verified: 'success', pass: 'success', passed: 'success', complete: 'success',
  fail: 'error', failed: 'error', failing: 'error', rejected: 'error', blocked: 'error',
  // #193: an agent PROPOSED archiving this item (MCP `archive_requirement` — human
  // confirm/cancel still pending). Must read as clearly distinct/urgent in the tree,
  // not blend in with "draft"/muted — reuses the `error` tone already used for
  // fail/rejected/blocked rather than adding a new colour.
  archive_proposed: 'error',
};

const CLASS_VAR = {
  muted: 'var(--text-3)', accent: 'var(--accent)',
  warning: 'var(--warning)', success: 'var(--success)', error: 'var(--error)',
};

/**
 * #569: the tree dot (and the item-detail title-row badge) now reflect the
 * APPROVAL rollup (`item_approvals`, via `GET /items/approval-states` /
 * `GET /items/{id}/breakdown`'s `approval_display_state`) instead of the old
 * free-text `status`/`verification_status` attribute — same 4 states
 * `core.requirements.feedback.approval_display_state` (backend) and
 * `reviewUtils.deriveApprovalState` (Breakdown tab) compute, same tone
 * convention the Breakdown tab's ApprovePill/review.css already use
 * (`.rmdv-appr.ok/.no/.stale` -> success/error/warning): green=approved
 * (fresh), red=not_approved, amber=approved-but-stale (content changed since
 * approval), grey=pending (no recorded approval, or none fresh).
 */
export const APPROVAL_TONE_CLASS = {
  approved: 'success',
  // F-09 / REQ-6099 (#603, D-08): an approval that SUPERSEDED another reviewer's open
  // rejection. It is a decided, approved state — but rendering it as plain green
  // "Approved" would erase the fact that the sign-off was contested, which is the whole
  // point of retaining both decisions. `accent` is already a first-class tone in ds.css
  // (`.dot.accent`, `.badge.accent`), so this needs no new design token and stays
  // unmistakable against success-green / error-red / warning-amber / muted-grey.
  approved_over_objection: 'accent',
  not_approved: 'error',
  stale: 'warning',
  pending: 'muted',
};

// F-23 / REQ-6109 (#607, WCAG 1.4.1 "Use of Colour"): every approval indicator
// needs a channel besides colour. One shared glyph set so the tree dot, the
// item-card dot and the link-type drawer's row status all agree on what each
// state looks like, not just what it's called — same states ApprovePill's icon
// set already uses (CheckCircleOutline/CancelOutlined/WarningAmber/RadioButtonUnchecked).
export const APPROVAL_DOT_GLYPH = {
  approved: '✓', not_approved: '✕', stale: '!', pending: '○',
  // F-09 / REQ-6099 + F-23 / REQ-6109: "approved, with an asterisk" — a single
  // character legible at the dot's 7px font size and shape-distinct from ✓ / ✕ / ! / ○,
  // so the contested sign-off is not signalled by the accent COLOUR alone (WCAG 1.4.1).
  approved_over_objection: '*',
};

const APPROVAL_LABEL = {
  approved: 'Approved',
  approved_over_objection: 'Approved over objection',
  not_approved: 'Not approved',
  pending: 'Pending',
};

// F-11 / REQ-6101 (#603): staleness is SYMMETRIC — the `stale` display state now covers a
// stale REJECTION as well as a stale approval, so the label may no longer hard-code
// "Approved · stale". Doing so told a reader their item was approved when a reviewer had
// in fact rejected it, which is worse than saying nothing. `staleState` is the state of
// the decision that went stale ('approved' | 'not_approved' | 'mixed'); it is absent on a
// surface that only holds the rolled-up state and on legacy payloads, and the fallback is
// then deliberately NEUTRAL rather than a guess.
const STALE_LABEL = {
  approved: 'Approved · stale',
  not_approved: 'Not approved · stale',
  mixed: 'Decisions stale',
};

/** F-11 / REQ-6101: label for the `stale` rollup, honest about WHICH decision lapsed. */
export function approvalStaleLabel(staleState) {
  return STALE_LABEL[staleState] || 'Decision stale';
}

/**
 * @param {string} state the approval rollup state
 * @param {?string} staleState F-11 / REQ-6101: which decision went stale, when known
 * @returns {{ className: string, tone: string, label: string, glyph: string }}
 */
export function approvalToneOf(state, staleState = null) {
  const className = APPROVAL_TONE_CLASS[state] || 'muted';
  const label = state === 'stale'
    ? approvalStaleLabel(staleState)
    : (APPROVAL_LABEL[state] || 'Pending');
  return { className, tone: CLASS_VAR[className], label, glyph: APPROVAL_DOT_GLYPH[state] || '' };
}

// D-06 / #582 (v1): the DERIVED, type-scoped verification indicator — evidence
// (a verifies/tests/validates trace link) rather than a hand-set string. Its OWN
// tone/glyph/label map, distinct from `APPROVAL_TONE_CLASS` above: approval is "a
// human signed off on the wording"; verification is "evidence the requirement is
// met" (#582's acceptance: the two are orthogonal, see docs/design/
// APPROVAL_TRACEABILITY.md). `null` ("not applicable" — the item's type has no
// verification semantics, e.g. a container or test_case) is handled by the CALLER
// checking for null and rendering nothing, exactly like the tree dot's "no entry"
// rule; this map only ever needs the two real states.
export const VERIFICATION_TONE_CLASS = {
  verified: 'success',
  unverified: 'muted',
};

// F-23 / REQ-6109 (#607, WCAG 1.4.1 "Use of Colour"): same discipline as
// APPROVAL_DOT_GLYPH — a shape-distinct glyph so the state is never conveyed by
// colour alone.
export const VERIFICATION_DOT_GLYPH = {
  verified: '✓',
  unverified: '○',
};

const VERIFICATION_LABEL = {
  verified: 'Verified',
  unverified: 'Not verified',
};

/**
 * @param {?string} state 'verified' | 'unverified' | null ("not applicable" —
 *   caller should render nothing for null, matching `GET /items/verification-states`'s
 *   three-state contract)
 * @returns {?{ className: string, tone: string, label: string, glyph: string }}
 *   null when `state` is null/undefined (nothing to render).
 */
export function verificationToneOf(state) {
  if (state == null) return null;
  const className = VERIFICATION_TONE_CLASS[state] || 'muted';
  return {
    className, tone: CLASS_VAR[className],
    label: VERIFICATION_LABEL[state] || 'Not verified',
    glyph: VERIFICATION_DOT_GLYPH[state] || '',
  };
}

// F-21 / REQ-6107 (#581): 'verified'/'unverified' are the RETIRED free-text concept
// #569 replaced with the `item_approvals` rollup (`approvalToneOf`, above) — an
// item whose `status` attribute still carries one of these two historical values
// (written by the now-deleted setVerified()/"Verify all") must not render as a
// second, disagreeing status dot/badge on every surface that calls `statusOf()`
// (item-card dots, the tree-grid's Status column, LinkTypeDrawer's per-row status —
// the #581 audit's F1-F4 findings). Every OTHER value this attribute legitimately
// carries for other item types (pass/fail/draft/archive_proposed/..., the audit's
// "verified clean" list) is untouched.
const RETIRED_STATUS_VALUES = new Set(['verified', 'unverified']);

/** @returns {{ label: string, className: string, tone: string }} */
export function statusOf(item) {
  const raw = (
    item?.status || item?.verification_status
    || item?.attributes?.status || item?.attributes?.verification_status || ''
  ).toString();
  if (RETIRED_STATUS_VALUES.has(raw.toLowerCase())) {
    return { label: '—', className: 'muted', tone: CLASS_VAR.muted };
  }
  const className = STATUS_TONE_CLASS[raw.toLowerCase()] || 'muted';
  // Snake_case values (e.g. #193's `archive_proposed`) read as a real label, not raw
  // code: spaces instead of underscores, first letter capitalised.
  const spaced = raw.replace(/_/g, ' ');
  const label = spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : '—';
  return { label, className, tone: CLASS_VAR[className] };
}
